// View as customer (ENG-2360). Exit in one tab removes the session cleanly, so
// another tab still showing the customer would write as the employee. Every
// request from a page loaded during a session names the identity that page
// shows; the server refuses a write whose identity is no longer current with
// 409 view_as_ended. Pages outside a session send nothing.

import type { VisitOptions } from "@inertiajs/core";

export const IDENTITY_HEADER = "X-Ordinal-Identity";

/** Rendered by the layout only while a session is live, as identityFor's "user:session". */
export function pageIdentity(): string | null {
  return document.querySelector<HTMLMetaElement>('meta[name="ordinal-identity"]')?.content || null;
}

function isSameOrigin(input: RequestInfo | URL): boolean {
  const url = input instanceof Request ? input.url : input.toString();
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Adds the header to same-origin fetches only, so third-party requests never
 * see it or need a CORS preflight for it.
 */
export function installIdentityFetchHeader(identity: string): () => void {
  const originalFetch = window.fetch;

  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    if (!isSameOrigin(input)) return originalFetch(input, init);

    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    headers.set(IDENTITY_HEADER, identity);
    return originalFetch(input, { ...init, headers });
  };

  return () => {
    window.fetch = originalFetch;
  };
}

/** Inertia's visitOptions default. Its headers replace the visit's, so keep the caller's. */
export function identityVisitOptions(identity: string) {
  return (href: string, options: VisitOptions): VisitOptions =>
    isSameOrigin(href) ? { headers: { ...options.headers, [IDENTITY_HEADER]: identity } } : {};
}
